/* eslint-disable react/prop-types */
/**
 * Design Pack Tab — v260.59
 * =========================
 * Sales → Implementation handover document. Renders an auto-assembled
 * snapshot of the estimate (cover, exec summary, requirements,
 * components, custom dev, integrations, planner, approvals) plus a
 * per-deliverable implementation checklist that consultants tick off.
 *
 * Two visual states:
 *   • DRAFT      → live preview, "Finalize Handover Pack" CTA visible
 *   • FINALIZED  → frozen snapshot, checklist statuses are editable,
 *                  super-admin sees an "Unlock" button
 *
 * Phase 1 (this) ships the data model + UI + finalize/unlock + checklist
 * updates. Phase 2 will add Word / PDF / Excel exports.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, Unlock, FileCheck2, Loader2, ChevronDown, ChevronRight,
  Building2, Target, ListChecks, CalendarCheck2,
  Check, Sparkles, Plug,
  RefreshCw, FileDown, Download, ArrowRight, Minus, Plus,
  Mail, X, Eye, FolderOpen, ExternalLink, Link as LinkIcon, Layers, Users as UsersIcon,
} from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { GanttChart } from '../../components/planner/GanttChart';
import DesignPackPreviewModal, { DesignPackPreviewBody } from './DesignPackPreviewModal';
import { fmtDate, fmtDateTime, SectionCard, KV } from './design-pack/shared';
import PlannerSnapshotSection, { ApprovalAuditSection } from './design-pack/PlannerSnapshotSection';
import ChecklistSection from './design-pack/ChecklistSection';
import EmailHandoverModal from './design-pack/EmailHandoverModal';
import SalesHandoverInternalSection from './design-pack/SalesHandoverInternalSection';
import { downloadAuthedFile } from '../../lib/fileDownload';
import detectLinkProvider from '../../lib/linkProvider';
import logger from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ──────────────────────────────────────────────────────────
// STATUS_META + CATEGORY_META live in ./design-pack/shared.js — they are
// consumed by ChecklistSection and any downstream UI that needs to
// render a deliverable badge/icon pair.






function CoverSection({ cover, metrics, finalizedAt, finalizedByName, status }) {
  return (
    <SectionCard id="cover" icon={Building2} title="Cover Sheet" defaultOpen>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KV label="Organisation" value={cover.organization_name} testid="design-pack-cover-org" />
        <KV label="Estimate" value={cover.estimate_name} testid="design-pack-cover-name" />
        <KV label="Implementation Type" value={cover.implementation_type} />
        <KV label="Complexity" value={cover.complexity_level} />
        <KV label="Target Audience" value={cover.target_audience?.toLocaleString?.() ?? cover.target_audience} />
        <KV label="Hosting Model" value={cover.hosting_model} />
        <KV label="Currency" value={cover.currency_code} />
        <KV label="Contracting Entity" value={cover.contracting_entity} />
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-4">
        <KV label="Total Effort (hrs)" value={metrics.total_hours?.toLocaleString?.()} testid="design-pack-metric-hours" />
        <KV label="Total Cost" value={metrics.total_cost ? `${cover.currency_code || ''} ${metrics.total_cost.toLocaleString?.()}` : null} testid="design-pack-metric-cost" />
        <KV label="Duration (wk)" value={metrics.duration_weeks} testid="design-pack-metric-duration" />
        <KV label="Go-Live" value={fmtDate(metrics.go_live_date)} testid="design-pack-metric-golive" />
        <KV label="Peak FTE" value={metrics.fte_count} />
      </div>

      {cover.implementation_description && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Implementation Description</div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{cover.implementation_description}</p>
        </div>
      )}

      {status === 'finalized' && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-2" data-testid="design-pack-finalize-meta">
          <Lock className="w-3 h-3 text-emerald-600" />
          Finalized {fmtDateTime(finalizedAt)} by <span className="font-medium text-[#0F2040]">{finalizedByName}</span>
        </div>
      )}
    </SectionCard>
  );
}

function CustomerAskSection({ requirements }) {
  if (!requirements) return null;
  const goals = requirements.business_goals || {};
  const hasGoals = (goals.achieve?.length || goals.success?.length || goals.roi?.length || goals.why_now?.length);
  const hasPainPoints = (requirements.pain_points || []).length > 0;
  const hasMetrics = (requirements.success_metrics || []).length > 0;
  const hasCustom = (requirements.custom_requirements || []).length > 0;

  return (
    <SectionCard id="customer-ask" icon={Target} title="What the customer asked for (the 'why')" defaultOpen>
      {hasGoals ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.achieve?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">What they want to achieve</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
                {goals.achieve.map((g, i) => <li key={`achieve-${i}-${g}`}>{g}</li>)}
              </ul>
            </div>
          )}
          {goals.success?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">What success looks like</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
                {goals.success.map((g, i) => <li key={`success-${i}-${g}`}>{g}</li>)}
              </ul>
            </div>
          )}
          {goals.roi?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Expected ROI / business case</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
                {goals.roi.map((g, i) => <li key={`roi-${i}-${g}`}>{g}</li>)}
              </ul>
            </div>
          )}
          {goals.why_now?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Why now</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
                {goals.why_now.map((g, i) => <li key={`why-now-${i}-${g}`}>{g}</li>)}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">No business goals captured in the Requirements form.</p>
      )}

      {hasPainPoints && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Pain points</div>
          <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
            {requirements.pain_points.map((p, i) => <li key={`pain-${i}-${p}`}>{p}</li>)}
          </ul>
        </div>
      )}

      {hasMetrics && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Success metrics</div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-1.5 pr-2">Metric</th>
                <th className="py-1.5 pr-2">Description</th>
                <th className="py-1.5 pr-2">Target</th>
              </tr>
            </thead>
            <tbody>
              {requirements.success_metrics.map((m, i) => (
                <tr key={m.metric || `metric-${i}`} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2 font-medium text-[#0F2040]">{m.metric}</td>
                  <td className="py-1.5 pr-2 text-slate-600">{m.description}</td>
                  <td className="py-1.5 pr-2 text-slate-700">{m.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasCustom && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Customer-stated custom requirements</div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-1.5 pr-2">ID</th>
                <th className="py-1.5 pr-2">Requirement</th>
                <th className="py-1.5 pr-2">Type</th>
                <th className="py-1.5 pr-2">Complexity</th>
              </tr>
            </thead>
            <tbody>
              {requirements.custom_requirements.map((c, i) => (
                <tr key={c.id || `cust-${i}`} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2 font-mono text-[10px] text-slate-500">{c.id || `CUST-${i + 1}`}</td>
                  <td className="py-1.5 pr-2 text-slate-700">{c.requirement}</td>
                  <td className="py-1.5 pr-2 text-slate-600">{c.type || '—'}</td>
                  <td className="py-1.5 pr-2 text-slate-600">{c.complexity || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ─── §17.5 Sales Context (Customer-facing) ─────────────────────────────
// Renders the Requirements form's sales_context section into the
// Design Pack so the implementation team sees the same facts the
// sales/CS team captured. Distinct from the internal MEDDPIC section
// below — this one is customer-shareable.
function ProjectContextSection({ context }) {
  if (!context) return null;
  const hasContent =
    context.why_seertech ||
    context.case_for_change?.length > 0 ||
    context.key_initiatives?.length > 0 ||
    context.first_time_to_value?.length > 0 ||
    context.quick_wins?.length > 0 ||
    context.products_use_case?.length > 0 ||
    context.hosting_location ||
    context.rollout_strategy ||
    context.learner_audience ||
    context.contract_term ||
    context.contract_special_clauses ||
    context.stakeholders?.length > 0 ||
    context.communication_plan ||
    context.risk_factors?.length > 0 ||
    context.risk_blockers ||
    context.prior_lms_experience;
  if (!hasContent) return null;

  return (
    <SectionCard
      id="project-context"
      icon={Target}
      title="Project Context (from Sales Handover)"
      defaultOpen={false}
    >
      {(context.why_seertech || context.case_for_change?.length > 0 || context.prior_lms_experience) && (
        <div className="space-y-3 mb-5">
          {context.why_seertech && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Why Seertech</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{context.why_seertech}</p>
            </div>
          )}
          {context.case_for_change?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Case for Change</div>
              <ul className="space-y-2">
                {context.case_for_change.map((row, i) => (
                  <li key={row.driver || `cfc-${i}`} className="border-l-2 border-[#4ABFCF] pl-3">
                    <div className="text-sm font-medium text-[#0F2040]">{row.driver || `Driver ${i + 1}`}</div>
                    {row.detail && (
                      <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{row.detail}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {context.prior_lms_experience && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Prior LMS Experience</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{context.prior_lms_experience}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 mb-4">
        {context.hosting_location && <KV label="Hosting Location" value={context.hosting_location} />}
        {context.rollout_strategy && <KV label="Rollout Strategy" value={context.rollout_strategy} />}
        {context.learner_audience && <KV label="Learner Audience" value={context.learner_audience} />}
        {context.contract_term && <KV label="Contract Term" value={context.contract_term} />}
        {context.contract_start_date && <KV label="Contract Start" value={fmtDate(context.contract_start_date)} />}
        {context.contract_end_date && <KV label="Contract End" value={fmtDate(context.contract_end_date)} />}
      </div>

      {context.contract_special_clauses && (
        <div className="mb-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Special Contract Clauses</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{context.contract_special_clauses}</p>
        </div>
      )}

      {(context.key_initiatives?.length > 0 || context.products_use_case?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pt-3 border-t border-slate-100">
          {context.key_initiatives?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Key Initiatives</div>
              <div className="flex flex-wrap gap-1.5">
                {context.key_initiatives.map((init, i) => (
                  <Badge key={`init-${i}-${init}`} variant="secondary" className="text-[10px]">{init}</Badge>
                ))}
              </div>
            </div>
          )}
          {context.products_use_case?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Products / Use Cases</div>
              <div className="flex flex-wrap gap-1.5">
                {context.products_use_case.map((p, i) => (
                  <Badge key={`use-case-${i}-${p}`} className="bg-indigo-50 text-indigo-700 text-[10px]">{p}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(context.first_time_to_value?.length > 0 || context.quick_wins?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pt-3 border-t border-slate-100">
          {context.first_time_to_value?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">First Time to Value — Top 5</div>
              <ol className="text-sm text-slate-700 list-decimal list-inside space-y-0.5">
                {context.first_time_to_value.map((g, i) => <li key={`fttv-${i}-${g}`}>{g}</li>)}
              </ol>
            </div>
          )}
          {context.quick_wins?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Quick Wins</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
                {context.quick_wins.map((g, i) => <li key={`qw-${i}-${g}`}>{g}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {context.stakeholders?.length > 0 && (
        <div className="mb-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Key Stakeholders</div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-1.5 pr-2">Role Type</th>
                <th className="py-1.5 pr-2">Title</th>
                <th className="py-1.5 pr-2">Name</th>
                <th className="py-1.5 pr-2">Email</th>
              </tr>
            </thead>
            <tbody>
              {context.stakeholders.map((s, i) => (
                <tr key={s.email || s.name || `stake-${i}`} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2 text-slate-600">{s.role_type || '—'}</td>
                  <td className="py-1.5 pr-2 text-slate-700">{s.title || '—'}</td>
                  <td className="py-1.5 pr-2 font-medium text-[#0F2040]">{s.name || '—'}</td>
                  <td className="py-1.5 pr-2">
                    {s.email ? (
                      <a href={`mailto:${s.email}`} className="text-sky-700 hover:underline">{s.email}</a>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
            {context.seertech_lives_in_org && <span>Seertech lives in: <span className="text-slate-700">{context.seertech_lives_in_org}</span></span>}
            {context.training_stakeholder_org && <span>Training org: <span className="text-slate-700">{context.training_stakeholder_org}</span></span>}
          </div>
        </div>
      )}

      {(context.core_team_in_place || context.weekly_hours_committed || context.team_time_zones || context.technical_resources_available || context.parallel_implementations || context.readiness_guide_provided) && (
        <div className="mb-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Customer Readiness</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
            {context.core_team_in_place && <KV label="Core team in place" value={context.core_team_in_place} />}
            {context.weekly_hours_committed && <KV label="Hours / week committed" value={context.weekly_hours_committed} />}
            {context.team_time_zones && <KV label="Time zones" value={context.team_time_zones} />}
            {context.technical_resources_available && <KV label="Technical resources" value={context.technical_resources_available} />}
            {context.readiness_guide_provided && <KV label="Readiness guide given" value={context.readiness_guide_provided} />}
          </div>
          {context.parallel_implementations && (
            <div className="mt-2 text-[11px] text-slate-600">
              <span className="text-slate-400">Concurrent implementations: </span>
              {context.parallel_implementations}
            </div>
          )}
        </div>
      )}

      {context.communication_plan && (
        <div className="mb-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Communication Plan</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{context.communication_plan}</p>
        </div>
      )}

      {(context.risk_factors?.length > 0 || context.risk_blockers || context.risk_mitigations) && (
        <div className="pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Risk Picture</div>
          {context.risk_factors?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {context.risk_factors.map((rf, i) => (
                <Badge key={`risk-${i}-${rf}`} className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">{rf}</Badge>
              ))}
            </div>
          )}
          {context.risk_blockers && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Possible blockers</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{context.risk_blockers}</p>
            </div>
          )}
          {context.risk_mitigations && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Mitigations</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{context.risk_mitigations}</p>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}


function DeliveryScopeSection({ components }) {
  // v260.62 — Two-level grouping (group → sub_group) mirroring the
  // Estimator layout. Components are nested under their sub_group when
  // present, else directly under the group. Per-component deliverables
  // are visually indented underneath as a bullet list with optional
  // per-deliverable assumption rows.
  const grouped = useMemo(() => {
    const out = new Map(); // group -> Map<sub_group, components[]>
    for (const c of components || []) {
      const g = c.group || 'Other';
      const sg = c.sub_group || '';
      if (!out.has(g)) out.set(g, new Map());
      const sub = out.get(g);
      if (!sub.has(sg)) sub.set(sg, []);
      sub.get(sg).push(c);
    }
    return Array.from(out.entries()).map(([g, subMap]) => ({
      group: g,
      subs: Array.from(subMap.entries()).map(([sg, comps]) => ({ sub_group: sg, components: comps })),
    }));
  }, [components]);

  return (
    <SectionCard id="components" icon={ListChecks} title="What we agreed to deliver" count={components.length} defaultOpen={false}>
      {grouped.map((g, gi) => (
        <div key={g.group} className={gi > 0 ? 'mt-5' : ''} data-testid={`design-pack-scope-group-${gi}`}>
          {/* Top-level group header — matches Estimator step heading
              style. */}
          <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">{g.group}</span>
            <span className="text-[10px] text-slate-400">
              {g.subs.reduce((sum, s) => sum + s.components.length, 0)} item{g.subs.reduce((sum, s) => sum + s.components.length, 0) === 1 ? '' : 's'}
            </span>
          </div>
          {g.subs.map(({ sub_group, components: comps }, si) => (
            <div key={sub_group || `_${si}`} className={si > 0 ? 'mt-3' : ''}>
              {sub_group && (
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 ml-1 font-semibold">{sub_group}</div>
              )}
              <div className="space-y-2">
                {comps.map((c, i) => (
                  <div key={c.name || `comp-${i}`} className="border border-slate-100 rounded-md p-2.5 bg-slate-50/40" data-testid={`design-pack-component-${gi}-${si}-${i}`}>
                    <div className="text-sm font-medium text-[#0F2040] flex items-center gap-1.5 flex-wrap">
                      {c.name}
                      {c.sub_group === 'Learning Intelligence' && (
                        <Badge className="bg-[#4ABFCF]/15 text-[#0F6B78] border border-[#4ABFCF]/40 text-[9px] py-0" data-testid={`design-pack-i-app-badge-${gi}-${si}-${i}`}>‘I’ app</Badge>
                      )}
                      {c.required && <Badge className="bg-rose-100 text-rose-700 text-[9px] py-0">Required</Badge>}
                      {c.role && <Badge variant="secondary" className="text-[9px] py-0">{c.role}</Badge>}
                      {c.hour_override != null && (
                        <span className="text-[10px] text-slate-400 ml-auto">{c.hour_override}h</span>
                      )}
                    </div>
                    {c.why && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{c.why}</div>}
                    {c.requires && (
                      <div className="text-[11px] text-[#0F6B78] mt-1 flex items-center gap-1" data-testid={`design-pack-requires-${gi}-${si}-${i}`}>
                        <LinkIcon className="w-3 h-3" /> Bundled with <span className="font-semibold">{c.requires}</span> — requires it to operate.
                      </div>
                    )}
                    {c.deliverables?.length > 0 && (
                      <ul className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1.5">
                        {c.deliverables.map((d, j) => (
                          <li key={d.text ? `${d.text}-${j}` : `del-${j}`} className="text-[11px] text-slate-700">
                            <div className="flex items-start gap-1.5">
                              <span className="text-slate-400 mt-0.5">›</span>
                              <div className="flex-1 min-w-0">
                                <span>{d.text}</span>
                                {d.is_milestone && <Badge className="ml-1.5 bg-amber-100 text-amber-700 text-[9px] py-0 align-middle">Milestone</Badge>}
                                {/* Per-deliverable assumptions — surface
                                    inline so consultants don't miss the
                                    caveats sales captured during the
                                    scoping. */}
                                {d.assumptions && d.assumptions.length > 0 && (
                                  <ul className="mt-1 ml-1 space-y-0.5">
                                    {d.assumptions.map((a, k) => (
                                      <li key={`asm-${k}-${a}`} className="text-[10px] text-slate-500 italic flex items-start gap-1">
                                        <span className="text-amber-500 mt-0.5">•</span>
                                        <span>Assumption: {a}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </SectionCard>
  );
}

function DeploymentWavesSection({ waves }) {
  if (!waves || !waves.waves?.length) return null;
  // Must match WAVE_COLORS in WavesPanel / GanttChart so waves read
  // consistently on screen and in exports.
  const WAVE_COLORS = ['#4ABFCF', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#3B82F6'];
  const fmtD = (iso) => {
    if (!iso) return null;
    try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; }
  };
  const statusMeta = {
    planned: { label: 'Planned', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
    in_flight: { label: 'In flight', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
    launched: { label: 'Launched', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  };
  const pay = waves.payment_schedule || [];
  return (
    <SectionCard id="waves" icon={Layers} title="Deployment Waves" count={waves.waves.length} defaultOpen={false} testid="design-pack-section-waves">
      <p className="text-[11px] text-slate-500 mb-3">
        The implementation scope is delivered in {waves.waves.length} deployment wave{waves.waves.length === 1 ? '' : 's'}.
        {waves.per_wave_lifecycle && ' Each wave runs its own Design → Build → UAT → Go-Live lifecycle.'}
      </p>
      <div className="space-y-3">
        {waves.waves.map((w, i) => (
          <div key={w.wave_id || i} className="border border-slate-100 rounded-md p-3 bg-slate-50/40" data-testid={`design-pack-wave-${i}`}>
            <div className="text-sm font-medium text-[#0F2040] flex items-center gap-2 flex-wrap">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: WAVE_COLORS[i % WAVE_COLORS.length] }} data-testid={`design-pack-wave-color-${i}`} />
              {w.name}
              {w.status && statusMeta[w.status] && (
                <Badge className={`text-[9px] border ${statusMeta[w.status].cls}`} data-testid={`design-pack-wave-status-${i}`}>
                  {statusMeta[w.status].label}
                </Badge>
              )}
              {w.status === 'launched' && w.actual_golive_date && (
                <span className="text-[10px] text-slate-500" data-testid={`design-pack-wave-actual-${i}`}>
                  Went live {fmtD(w.actual_golive_date)}
                  {w.slip_days != null && (
                    <span className={`ml-1 font-semibold ${w.slip_days > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {w.slip_days === 0 ? '(on time)' : w.slip_days > 0 ? `(${w.slip_days}d late)` : `(${Math.abs(w.slip_days)}d early)`}
                    </span>
                  )}
                </span>
              )}
              {w.total_effort != null && <Badge variant="secondary" className="text-[10px]">{w.total_effort}h</Badge>}
              {w.total_cost > 0 && (
                <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`design-pack-wave-cost-${i}`}>
                  ${Math.round(w.total_cost).toLocaleString()}
                </Badge>
              )}
              {w.start_week && (
                <span className="text-[11px] text-slate-500">
                  W{w.start_week}–W{w.end_week} ({w.duration_weeks} wks)
                  {w.start_date && <span className="text-slate-400"> · {fmtD(w.start_date)} → {fmtD(w.end_date)}</span>}
                </span>
              )}
            </div>
            {w.description && <div className="text-xs text-slate-600 mt-1">{w.description}</div>}
            {w.components?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {w.components.map((c, j) => (
                  <Badge key={`${c.name}-${j}`} variant="outline" className="text-[10px] border-slate-200 text-slate-600">
                    {c.name}{c.fraction != null && c.fraction < 0.999 ? ` (${Math.round(c.fraction * 100)}%)` : ''} · {c.effort}h{c.fine_tuned && c.start_week != null ? ` · W${c.start_week}–${c.end_week}` : ''}
                  </Badge>
                ))}
              </div>
            )}
            {(w.roles?.length > 0 || w.client_resources?.length > 0) && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {w.roles?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Seertech roles</div>
                    <div className="flex flex-wrap gap-1">
                      {w.roles.map(r => <Badge key={r.role} variant="outline" className="text-[10px]">{r.role_label}: {r.effort}h</Badge>)}
                    </div>
                  </div>
                )}
                {w.client_resources?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Client resources</div>
                    <div className="flex flex-wrap gap-1">
                      {w.client_resources.map(cr => <Badge key={cr.role} variant="outline" className="text-[10px] border-slate-200 text-slate-600">{cr.label}: {cr.total_hours}h</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {waves.unallocated?.length > 0 && (
        <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="design-pack-waves-unallocated">
          Not yet allocated to a wave: {waves.unallocated.slice(0, 6).map(u => `${u.name} (${u.remaining_hours}h)`).join(', ')}{waves.unallocated.length > 6 ? '…' : ''}
        </div>
      )}
      {pay.length > 0 && (
        <div className="mt-4" data-testid="design-pack-payment-schedule">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040] pb-1.5 border-b border-slate-200 mb-2">Payment Schedule</div>
          <p className="text-[10px] text-slate-500 mb-2">Each deployment wave is invoiced at its go-live milestone.</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="text-left py-1 font-semibold">Invoice trigger</th>
                <th className="text-left py-1 font-semibold">Timing</th>
                <th className="text-right py-1 font-semibold">Amount</th>
                <th className="text-right py-1 font-semibold">% of schedule</th>
                <th className="text-right py-1 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {pay.map((r, i) => (
                <tr key={r.wave_id || `p-${i}`} className="border-b border-slate-100" data-testid={`design-pack-payment-row-${i}`}>
                  <td className="py-1.5 font-medium text-[#0F2040]">{r.trigger}</td>
                  <td className="py-1.5 text-slate-500">{r.date ? fmtD(r.date) : r.week ? `Week ${r.week}` : '—'}</td>
                  <td className="py-1.5 text-right font-semibold text-[#0F2040]">${Math.round(r.amount || 0).toLocaleString()}</td>
                  <td className="py-1.5 text-right text-slate-500">{r.pct_of_schedule}%</td>
                  <td className="py-1.5 text-right">
                    {r.status && statusMeta[r.status] ? (
                      <Badge className={`text-[9px] border ${statusMeta[r.status].cls}`}>{statusMeta[r.status].label}</Badge>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="py-1.5 font-bold text-[#0F2040]">Total</td>
                <td />
                <td className="py-1.5 text-right font-bold text-[#0F2040]">${Math.round(pay.reduce((s, r) => s + (r.amount || 0), 0)).toLocaleString()}</td>
                <td className="py-1.5 text-right font-bold text-[#0F2040]">100%</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function CustomDevSection({ items }) {
  if (!items?.length) return null;
  return (
    <SectionCard id="custom-dev" icon={Sparkles} title="Custom development" count={items.length} defaultOpen={false}>
      <ul className="space-y-2">
        {items.map((nd, i) => (
          <li key={nd.name || `nd-${i}`} className="border border-slate-100 rounded-md p-2.5 bg-slate-50/40">
            <div className="text-sm font-medium text-[#0F2040]">{nd.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Size: <span className="font-medium">{nd.size}</span>
              {nd.hour_override != null && <> · Hours: {nd.hour_override}</>}
              {nd.planner_weeks && <> · Duration: {nd.planner_weeks}w</>}
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function IntegrationsSection({ items }) {
  if (!items?.length) return null;
  return (
    <SectionCard id="integrations" icon={Plug} title="Integrations" count={items.length} defaultOpen={false}>
      <ul className="space-y-2">
        {items.map((ig, i) => (
          <li key={ig.label || `ig-${i}`} className="border border-slate-100 rounded-md p-2.5 bg-slate-50/40">
            <div className="text-sm font-medium text-[#0F2040]">{ig.label}</div>
            {ig.notes && <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{ig.notes}</div>}
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function SourceDocumentsSection({ estimateId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/source-documents`,
          { withCredentials: true },
        );
        if (mounted) setDocs(r.data.documents || []);
      } catch (e) {
        logger.warn('source documents fetch failed', e?.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [estimateId]);

  const handleDownload = async (doc) => {
    try {
      await downloadAuthedFile(
        `/api/estimates/${estimateId}/source-documents/${doc.id}/download`,
        doc.filename,
      );
    } catch (e) {
      toast.error('Could not download the document');
    }
  };

  // Render nothing while loading or when no source docs are attached, so
  // the pack stays clean for estimates without supporting files.
  if (loading || docs.length === 0) return null;

  return (
    <SectionCard
      id="source-documents"
      icon={FolderOpen}
      title="Source Documents"
      count={docs.length}
      defaultOpen={false}
    >
      <p className="text-[11px] text-slate-500 mb-3">
        The supporting documents that informed this estimate&apos;s requirements. Click any
        document to open it (files require a Blueprint login; links open in a new tab).
      </p>
      <ul className="space-y-1.5" data-testid="design-pack-source-documents-list">
        {docs.map((doc) => {
          const isLink = doc.kind === 'link';
          const provider = isLink ? detectLinkProvider(doc.url) : null;
          const LinkIcon = provider ? provider.Icon : ExternalLink;
          return (
            <li key={doc.id}>
              <button
                type="button"
                onClick={() => (isLink
                  ? window.open(doc.url, '_blank', 'noopener,noreferrer')
                  : handleDownload(doc))}
                className="w-full flex items-center gap-2 text-left p-2 rounded-md border border-slate-100 bg-slate-50/40 hover:border-[#4ABFCF]/50 hover:bg-[#4ABFCF]/5 transition-colors group"
                data-testid={`design-pack-source-doc-${doc.id}`}
                title={isLink ? `Open ${doc.url}` : `Download ${doc.filename}`}
              >
                {isLink
                  ? <LinkIcon className={`w-3.5 h-3.5 shrink-0 ${provider.className}`} />
                  : <FileDown className="w-3.5 h-3.5 text-[#4ABFCF] shrink-0" />}
                <span className="text-sm text-[#0F2040] group-hover:underline truncate flex-1 min-w-0">
                  {doc.filename}
                </span>
                {isLink && (
                  <span className="text-[10px] text-slate-400 shrink-0">{provider.label}</span>
                )}
                <span className="text-[10px] text-slate-400 shrink-0">
                  {doc.uploaded_by} · {fmtDate(doc.uploaded_at)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}

function FinalizeModal({ pack, onClose, onConfirm, submitting }) {
  const counts = useMemo(() => {
    const snap = pack?.snapshot || {};
    return {
      components: (snap.components || []).length,
      newDev: (snap.new_developments || []).length,
      integrations: (snap.integrations || []).length,
      approvals: (snap.approval_audit || []).filter(a => a.kind === 'approved' || a.kind === 'changes_requested').length,
      checklist: (pack?.checklist || []).length,
    };
  }, [pack]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="design-pack-finalize-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <Lock className="w-4 h-4 text-emerald-600" />
          <h2 className="text-sm font-semibold text-[#0F2040]">Finalize Handover Pack</h2>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p className="text-slate-700">
            This will <strong>freeze a snapshot</strong> of the current estimate and generate
            an editable implementation checklist for the consultant team.
          </p>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Components</span><span className="font-medium text-[#0F2040]">{counts.components}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Custom development</span><span className="font-medium text-[#0F2040]">{counts.newDev}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Integrations</span><span className="font-medium text-[#0F2040]">{counts.integrations}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Approval events</span><span className="font-medium text-[#0F2040]">{counts.approvals}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5">
              <span className="text-slate-700 font-medium">Total checklist rows</span>
              <span className="font-bold text-[#0F2040]">{counts.checklist}</span>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Once finalized, the snapshot is locked. Subsequent estimate changes won&apos;t
            reflect in the pack. A super-admin can unlock if needed.
          </p>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50/40">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting} data-testid="design-pack-finalize-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="design-pack-finalize-confirm"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Lock className="w-3.5 h-3.5 mr-1" />}
            Finalize
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Email Handover Modal ─────────────────────────────────────────────

function RegenerateModal({ diff, loading, onClose, onConfirm, applying }) {
  const d = diff?.diff || null;
  const mc = d?.metric_changes || {};

  // Helper — render an old → new pair only when it actually changed.
  const changed = (key) => mc[key] && mc[key].old !== mc[key].new;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="design-pack-regenerate-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-[#0F2040]" />
          <h2 className="text-sm font-semibold text-[#0F2040]">Regenerate from current estimate</h2>
        </div>
        <div className="p-4 overflow-y-auto space-y-3 text-sm">
          {loading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500 text-xs">Computing diff…</span>
            </div>
          ) : !d ? (
            <p className="text-slate-500 text-xs">No diff returned.</p>
          ) : (
            <>
              <p className="text-slate-700">
                This will <strong>replace the locked snapshot</strong> with the current
                estimate state. Checklist statuses + notes are <strong>preserved</strong> for
                deliverables that still exist (matched on category + name).
              </p>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
                <div className="font-semibold text-[#0F2040] text-[11px] uppercase tracking-wide">Components</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex items-center gap-1 text-emerald-700"><Plus className="w-3 h-3" /> {(d.components_added || []).length} added</div>
                  <div className="flex items-center gap-1 text-rose-700"><Minus className="w-3 h-3" /> {(d.components_removed || []).length} removed</div>
                  <div className="text-slate-500">{d.components_unchanged_count} unchanged</div>
                </div>
                {(d.components_added || []).length > 0 && (
                  <ul className="text-[11px] text-emerald-700 list-disc list-inside pl-1">
                    {d.components_added.slice(0, 8).map(n => <li key={n}>{n}</li>)}
                    {d.components_added.length > 8 && <li className="italic text-slate-400">+ {d.components_added.length - 8} more</li>}
                  </ul>
                )}
                {(d.components_removed || []).length > 0 && (
                  <ul className="text-[11px] text-rose-700 list-disc list-inside pl-1">
                    {d.components_removed.slice(0, 8).map(n => <li key={n}>{n}</li>)}
                    {d.components_removed.length > 8 && <li className="italic text-slate-400">+ {d.components_removed.length - 8} more</li>}
                  </ul>
                )}
              </div>

              {((d.new_dev_added || []).length > 0 || (d.new_dev_removed || []).length > 0) && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                  <div className="font-semibold text-[#0F2040] text-[11px] uppercase tracking-wide mb-1">Custom Dev</div>
                  {(d.new_dev_added || []).map(n => <div key={n} className="text-emerald-700 flex items-center gap-1"><Plus className="w-3 h-3" />{n}</div>)}
                  {(d.new_dev_removed || []).map(n => <div key={n} className="text-rose-700 flex items-center gap-1"><Minus className="w-3 h-3" />{n}</div>)}
                </div>
              )}

              {((d.integrations_added || []).length > 0 || (d.integrations_removed || []).length > 0) && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                  <div className="font-semibold text-[#0F2040] text-[11px] uppercase tracking-wide mb-1">Integrations</div>
                  {(d.integrations_added || []).map(n => <div key={n} className="text-emerald-700 flex items-center gap-1"><Plus className="w-3 h-3" />{n}</div>)}
                  {(d.integrations_removed || []).map(n => <div key={n} className="text-rose-700 flex items-center gap-1"><Minus className="w-3 h-3" />{n}</div>)}
                </div>
              )}

              {(changed('total_hours') || changed('total_cost') || changed('duration_weeks') || changed('go_live_date')) && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                  <div className="font-semibold text-[#0F2040] text-[11px] uppercase tracking-wide mb-1">Metrics</div>
                  {changed('total_hours') && <div className="flex items-center gap-1">Effort: <span className="text-slate-500">{mc.total_hours.old ?? '—'}</span> <ArrowRight className="w-3 h-3" /> <span className="font-medium text-[#0F2040]">{mc.total_hours.new ?? '—'}</span> h</div>}
                  {changed('total_cost') && <div className="flex items-center gap-1">Cost: <span className="text-slate-500">{mc.total_cost.old ?? '—'}</span> <ArrowRight className="w-3 h-3" /> <span className="font-medium text-[#0F2040]">{mc.total_cost.new ?? '—'}</span></div>}
                  {changed('duration_weeks') && <div className="flex items-center gap-1">Duration: <span className="text-slate-500">{mc.duration_weeks.old ?? '—'}</span> <ArrowRight className="w-3 h-3" /> <span className="font-medium text-[#0F2040]">{mc.duration_weeks.new ?? '—'}</span> wk</div>}
                  {changed('go_live_date') && <div className="flex items-center gap-1">Go-Live: <span className="text-slate-500">{mc.go_live_date.old ?? '—'}</span> <ArrowRight className="w-3 h-3" /> <span className="font-medium text-[#0F2040]">{mc.go_live_date.new ?? '—'}</span></div>}
                </div>
              )}

              {(d.components_added.length + d.components_removed.length + d.new_dev_added.length + d.new_dev_removed.length + d.integrations_added.length + d.integrations_removed.length === 0
                && !changed('total_hours') && !changed('total_cost') && !changed('duration_weeks') && !changed('go_live_date')) && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  No changes detected — the snapshot is already in sync with the current estimate. Regenerating will just refresh AI-baked rationale.
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50/40">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={applying} data-testid="design-pack-regenerate-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={loading || applying || !d}
            className="bg-[#0F2040] hover:bg-[#1A3055]"
            data-testid="design-pack-regenerate-apply"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Apply Regenerate
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab Component ───────────────────────────────────────────────

export default function DesignPackTab() {
  const { estimateId } = useParams();
  const { user } = useAuth();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinalize, setShowFinalize] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  // v260.64 — preview modal: null when closed, or one of 'docx'|'pdf'|'xlsx'.
  const [previewKind, setPreviewKind] = useState(null);
  const [regenDiff, setRegenDiff] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenApplying, setRegenApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isFinalized = pack?.status === 'finalized';
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchPack = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/design-pack`,
        { withCredentials: true },
      );
      setPack(r.data);
    } catch (e) {
      logger.warn('design pack fetch failed', e?.message);
      toast.error('Could not load Design Pack');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { fetchPack(); }, [fetchPack]);

  const handleFinalize = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/design-pack/finalize`,
        {},
        { withCredentials: true },
      );
      toast.success(`Handover Pack finalized · ${r.data?.checklist_size || 0} checklist items`);
      setShowFinalize(false);
      await fetchPack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Unlock this Handover Pack? The snapshot will become editable again.')) return;
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/design-pack/unlock`,
        {},
        { withCredentials: true },
      );
      toast.success('Handover Pack unlocked');
      await fetchPack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlock failed');
    }
  };

  const handleChecklistUpdate = useCallback(async (itemId, patch) => {
    try {
      const r = await axios.patch(
        `${API_URL}/api/estimates/${estimateId}/design-pack/checklist/${itemId}`,
        patch,
        { withCredentials: true },
      );
      setPack(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          checklist: prev.checklist.map(it => it.id === itemId ? r.data.item : it),
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  }, [estimateId]);

  // Export — fetches the file as a blob and triggers a browser download.
  // Reused by Word / PDF / Excel buttons.
  const handleExport = useCallback(async (kind) => {
    const path =
      kind === 'docx' ? 'export/docx' :
      kind === 'pdf' ? 'export/pdf' :
      'export/checklist.xlsx';
    const label = kind === 'docx' ? 'Word doc' : kind === 'pdf' ? 'PDF' : 'Excel checklist';
    try {
      const r = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/design-pack/${path}`,
        { withCredentials: true, responseType: 'blob' },
      );
      // Pull filename from Content-Disposition if present
      const disp = r.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m?.[1] || `design_pack.${kind === 'checklist.xlsx' ? 'xlsx' : kind}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Downloaded ${label}`);
    } catch (e) {
      toast.error(`${label} export failed`);
    }
  }, [estimateId]);

  // Regenerate flow — two-step (preview diff → apply). Opens a modal
  // showing what would change before mutating the locked snapshot, so
  // sales doesn't accidentally clobber a signed-off pack.
  const handleRegeneratePreview = useCallback(async () => {
    setRegenLoading(true);
    setRegenDiff(null);
    setShowRegenerate(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/design-pack/regenerate`,
        {},
        { withCredentials: true },
      );
      setRegenDiff(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate preview failed');
      setShowRegenerate(false);
    } finally {
      setRegenLoading(false);
    }
  }, [estimateId]);

  const handleRegenerateApply = useCallback(async () => {
    setRegenApplying(true);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/design-pack/regenerate?apply=true`,
        {},
        { withCredentials: true },
      );
      toast.success('Handover Pack regenerated from current estimate');
      setShowRegenerate(false);
      setRegenDiff(null);
      await fetchPack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    } finally {
      setRegenApplying(false);
    }
  }, [estimateId, fetchPack]);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center" data-testid="design-pack-loading">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-slate-400" data-testid="design-pack-empty">
        Could not load Design Pack.
      </div>
    );
  }

  const { snapshot, checklist } = pack;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="design-pack-tab">
      {/* Status banner */}
      <div
        className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
          isFinalized
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}
        data-testid={`design-pack-banner-${isFinalized ? 'finalized' : 'draft'}`}
      >
        <div className="flex items-center gap-2">
          {isFinalized ? <Lock className="w-4 h-4 text-emerald-600" /> : <FileCheck2 className="w-4 h-4 text-amber-600" />}
          <div>
            <div className="text-sm font-semibold text-[#0F2040]">
              {isFinalized ? 'Handover Pack — Finalized' : 'Handover Pack — Draft preview'}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {isFinalized
                ? <>Locked on {fmtDateTime(pack.finalized_at)} by <span className="font-medium">{pack.finalized_by_name}</span></>
                : 'Reflects the current estimate. Click Finalize to lock the snapshot and activate the checklist.'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Exports — available in both draft and finalized modes so
              sales can preview the output before locking. Each format
              has a Download button (one-click save) and a small Eye
              button that opens an inline first-page preview so the
              consultant can sanity-check the file before sending. */}
          <div className="flex items-center gap-1 mr-1" data-testid="design-pack-export-group">
            {[
              { kind: 'docx', label: 'Word', icon: FileDown, title: 'Download as Word document' },
              { kind: 'pdf', label: 'PDF', icon: FileDown, title: 'Download as PDF' },
              { kind: 'xlsx', label: 'Excel', icon: Download, title: 'Download checklist as Excel' },
            ].map(({ kind, label, icon: Icon, title }) => (
              <div
                key={kind}
                className="inline-flex rounded-md border border-slate-200 overflow-hidden"
                data-testid={`design-pack-export-cluster-${kind}`}
              >
                <button
                  type="button"
                  onClick={() => handleExport(kind)}
                  data-testid={`design-pack-export-${kind}-btn`}
                  title={title}
                  className="h-8 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-1"
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewKind(kind)}
                  data-testid={`design-pack-preview-${kind}-btn`}
                  title={`Preview ${label} before downloading`}
                  className="h-8 px-1.5 border-l border-slate-200 text-slate-500 hover:bg-[#4ABFCF]/10 hover:text-[#0F2040] flex items-center"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {/* Email Handover Pack — one click sends Word + Excel
                attachments to the implementation lead via Resend,
                CC'ing the approval stakeholders from the audit trail. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEmail(true)}
              data-testid="design-pack-email-btn"
              title="Email handover pack to implementation team"
              className="border-[#4ABFCF]/40 text-[#0F2040] hover:bg-[#4ABFCF]/10 ml-1"
            >
              <Mail className="w-3.5 h-3.5 mr-1" /> Email
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchPack}
            data-testid="design-pack-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {isFinalized && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegeneratePreview}
              className="text-slate-600 hover:text-[#0F2040]"
              data-testid="design-pack-regenerate-btn"
              title="Regenerate snapshot from the current estimate"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate
            </Button>
          )}
          {isFinalized && isSuperAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUnlock}
              className="text-slate-600 hover:text-rose-700"
              data-testid="design-pack-unlock-btn"
            >
              <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
            </Button>
          )}
          {!isFinalized && (
            <Button
              size="sm"
              onClick={() => setShowFinalize(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="design-pack-finalize-btn"
            >
              <Lock className="w-3.5 h-3.5 mr-1" /> Finalize Handover Pack
            </Button>
          )}
        </div>
      </div>

      {/* Sales → Delivery Handover — surfaced at the very top of the
          Design Pack: it's the most important artefact on this page, so
          consultants land on it first. */}
      <SalesHandoverInternalSection estimateId={estimateId} currentUser={user} />

      {/* Sections */}
      <CoverSection
        cover={snapshot.cover}
        metrics={snapshot.metrics}
        finalizedAt={pack.finalized_at}
        finalizedByName={pack.finalized_by_name}
        status={pack.status}
      />
      <CustomerAskSection requirements={snapshot.requirements} />
      <SourceDocumentsSection estimateId={estimateId} />
      <ProjectContextSection context={snapshot.requirements?.sales_context} />
      <DeliveryScopeSection components={snapshot.components || []} />
      <DeploymentWavesSection waves={snapshot.waves} />
      <CustomDevSection items={snapshot.new_developments || []} />
      <IntegrationsSection items={snapshot.integrations || []} />
      <PlannerSnapshotSection planner={snapshot.planner || {}} estimateId={estimateId} />
      <ApprovalAuditSection events={snapshot.approval_audit || []} />
      <ChecklistSection
        checklist={checklist || []}
        onUpdate={handleChecklistUpdate}
        disabled={!isFinalized}
      />

      {showFinalize && (
        <FinalizeModal
          pack={pack}
          onClose={() => setShowFinalize(false)}
          onConfirm={handleFinalize}
          submitting={submitting}
        />
      )}

      {showRegenerate && (
        <RegenerateModal
          diff={regenDiff}
          loading={regenLoading}
          applying={regenApplying}
          onClose={() => { setShowRegenerate(false); setRegenDiff(null); }}
          onConfirm={handleRegenerateApply}
        />
      )}

      {showEmail && (
        <EmailHandoverModal
          estimateId={estimateId}
          onClose={() => setShowEmail(false)}
        />
      )}

      {previewKind && (
        <DesignPackPreviewModal
          kind={previewKind}
          estimateId={estimateId}
          onClose={() => setPreviewKind(null)}
        />
      )}
    </div>
  );
}
